import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Lock, Sparkles, Tag, Trophy, ShoppingCart, Palette, Share2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import SkinPreview from "../components/SkinPreview";
import LoadoutsPanel from "../components/LoadoutsPanel";
import ShieldStore from "../components/ShieldStore";
import MoonstoneStore from "../components/MoonstoneStore";
import HalloweenBundleCard from "../components/HalloweenBundleCard";
import api, { formatApiErrorDetail } from "../lib/api";
import { shareSkin } from "../lib/skinShare";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  { id: "all", label: "All" },
  { id: "free", label: "Free" },
  { id: "premium", label: "Premium" },
  { id: "legendary", label: "Legendary" },
  { id: "trophy", label: "Trophy" },
  { id: "prismatic", label: "Prismatic" },
];

// Friendly sector labels keyed by drop_level so prismatic skin cards can
// surface "Defeat Sector 1 Boss · Lvl 500" instead of the raw number.
const PRISMATIC_SECTOR_LABEL = {
  500: "Sector 1 Boss · Lvl 500",
  1000: "Sector 2 Boss · Lvl 1000",
  1500: "Sector 3 Boss · Lvl 1500",
  2000: "Sector 4 Boss · Lvl 2000",
  2500: "Sector 5 Boss · Lvl 2500",
  3000: "Sector 6 Boss · Lvl 3000",
};

// Human-readable mapping from achievement id → achievement display name.
// Used on trophy-tier cards to tell the user which achievement unlocks them.
const ACH_NAME = {
  grandmaster: "Cosmic Grandmaster",
  "thousand-breaker": "Thousand Breaker",
  "daily-streak-7": "7-Day Cosmic Streak",
  "perfect-month": "Perfect Month",
  "endless-flawless-hunter": "Flawless Hunter (×5 streak, wave 30+)",
  "endless-wormhole-slayer": "Wormhole Slayer (rare-worm kill)",
  "endless-immortal": "Endless Immortal (wave 50)",
  "nemesis-slain": "Nemesis Slayer (3-revenge avenged)",
  "sector-reaper": "Sector Reaper (all 6 bosses)",
};

// One-line in-app lore for select trophy skins, keyed by skin id. Surfaces
// on the locked card as a small italic line above the unlock criterion —
// makes the cosmetic feel earned rather than transactional. Kept short
// (≤ ~110 chars) so it fits the same card footprint at mobile widths.
const SKIN_LORE = {
  "flawless-hunter":
    "Worn by pilots who chained five cosmic milestones without a single death — deep into the asteroid belt.",
  "wormhole-slayer":
    "Awarded for striking down the rare teleporting Wormhole Worm before its sky cleared — a singularity sealed.",
  "reaper-crown":
    "Forged on the bones of every Leviathan. Only the pilots who closed all six sector vaults wear it.",
  "endless-immortal":
    "The fiftieth wave breaks most pilots. The pilots who break the fiftieth wave wear this.",
  "nemesis-slain":
    "Three losses, three answers. Marked the night a single rival was finally laid to rest.",
};

// Swatches offered in the trail-color picker. "default" resets to the skin's
// native palette. Keep the list ~8 entries so the row still fits on mobile.
const TRAIL_SWATCHES = [
  { id: "default", label: "Default", value: null },
  { id: "cyan",    label: "Cyan",    value: "#00ffff" },
  { id: "pink",    label: "Pink",    value: "#ff007f" },
  { id: "gold",    label: "Gold",    value: "#ffd700" },
  { id: "purple",  label: "Purple",  value: "#b026ff" },
  { id: "emerald", label: "Emerald", value: "#00ff88" },
  { id: "orange",  label: "Orange",  value: "#ff8c1a" },
  { id: "ice",     label: "Ice",     value: "#a8e4ff" },
];

// Return a copy of the skin with the primary flame colour swapped for the
// user's custom trail colour. Passing null returns the skin unchanged.
export function applyTrailColor(skin, color) {
  if (!skin || !color) return skin;
  const flame = Array.isArray(skin.flame) ? [...skin.flame] : ["#ff007f", "#ff007f"];
  flame[0] = color;
  return { ...skin, flame };
}

// One-tap share button — generates a 1200x630 social card and either
// invokes the native Web Share sheet (mobile) or copies share text and
// downloads the PNG (desktop). Surfaces a friendly toast either way.
function ShareButton({ skin, refCode }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async (e) => {
    // Prevent the click from bubbling to a parent card-click handler.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const result = await shareSkin(skin, refCode);
      if (result === "shared") {
        toast.success(`Shared "${skin.name}"`);
      } else if (result === "copied") {
        toast.success(`"${skin.name}" image saved + caption copied`, {
          description: "Drop the PNG into your tweet/Discord — caption is on your clipboard.",
          duration: 4500,
        });
      }
      // "cancelled" → silent (the user backed out of the share sheet)
    } catch (err) {
      toast.error("Couldn't generate the share card", {
        description: err?.message ? String(err.message).slice(0, 100) : "Try again in a moment.",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className="px-3 py-2 border border-white/15 hover:border-[#b026ff] hover:text-[#b026ff] text-white/70 transition disabled:opacity-50"
      title={`Share ${skin.name}${refCode ? " · earn +50 ☾ when a friend signs up" : ""}`}
      aria-label={`Share ${skin.name}`}
      data-testid={`skin-share-${skin.id}`}
    >
      <Share2 className="w-3.5 h-3.5" />
    </button>
  );
}

export default function Skins() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { user } = useAuth();
  const refCode = user?.ref_code || null;
  const [data, setData] = useState(null);
  const [paddleAccent, setPaddleAccentState] = useState(null);
  const [filter, setFilter] = useState("all");
  // Top-level tab — "comets" shows the comet/ball-skin vault, "moonstone"
  // surfaces the Moonstone Store (Endless-mode wallet, run-starters, perks
  // and moonstone cosmetic skins).
  const [view, setView] = useState("comets");
  const [busy, setBusy] = useState(false);
  const [payStatus, setPayStatus] = useState(null); // { message, tone }

  const refresh = useCallback(async () => {
    const [skinsRes, cosmRes] = await Promise.all([
      api.get("/ball-skins"),
      api.get("/cosmetics").catch(() => ({ data: { paddle_accent: null } })),
    ]);
    setData(skinsRes.data);
    setPaddleAccentState(cosmRes.data?.paddle_accent || null);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Handle Stripe return: poll session status
  const pollingSidRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(search);
    const sid = params.get("session_id");
    const product = params.get("product"); // "shield" for shield-store purchases
    const canceled = params.get("canceled");
    if (canceled) {
      setPayStatus({ message: "Purchase canceled.", tone: "warn" });
      navigate("/skins", { replace: true });
      return;
    }
    if (!sid || pollingSidRef.current === sid) return;
    pollingSidRef.current = sid;
    const isShield = product === "shield";
    const isHalloween = product === "halloween";
    let attempts = 0;
    setPayStatus({ message: "Confirming your purchase...", tone: "info" });
    const poll = async () => {
      try {
        if (isHalloween) {
          // Halloween bundle status uses the ownership snapshot rather
          // than a per-session endpoint — the webhook / verify-session
          // path stamps `owned_bundles` server-side. Poll status until
          // ownership flips.
          const r = await api.get(`/halloween/verify-session/${sid}`);
          if (r.data.owned) {
            setPayStatus({ message: "Halloween bundle unlocked & equipped! Pumpkin bombs armed.", tone: "ok" });
            await refresh();
            navigate("/skins", { replace: true });
            return;
          }
          if (r.data.status === "expired") {
            setPayStatus({ message: "Checkout expired. Try again.", tone: "warn" });
            navigate("/skins", { replace: true });
            return;
          }
        } else {
          const endpoint = isShield
            ? `/shields/checkout/status/${sid}`
            : `/ball-skins/checkout/status/${sid}`;
          const r = await api.get(endpoint);
          if (r.data.payment_status === "paid") {
            setPayStatus({
              message: isShield
                ? `Streak Shields added! +${r.data.quantity || 1} charge${r.data.quantity === 1 ? "" : "s"}`
                : "Skin unlocked! Equip it below.",
              tone: "ok",
            });
            await refresh();
            navigate("/skins", { replace: true });
            return;
          }
          if (r.data.status === "expired") {
            setPayStatus({ message: "Checkout expired. Try again.", tone: "warn" });
            navigate("/skins", { replace: true });
            return;
          }
        }
      } catch (e) {
        if (isHalloween && e.response?.status === 400) {
          setPayStatus({ message: "Couldn't find that checkout. Try again.", tone: "warn" });
          navigate("/skins", { replace: true });
          return;
        }
      }
      attempts++;
      if (attempts < 10) setTimeout(poll, 2000);
      else {
        setPayStatus({ message: "Still processing — refresh in a moment.", tone: "warn" });
        navigate("/skins", { replace: true });
      }
    };
    poll();
  }, [search, navigate, refresh]);

  // Share-deep-link handler: when arriving with `?skin=<id>` (typically
  // from a shared social card), wait for the catalog to render then
  // scroll the matching card into view and pulse it to draw the eye.
  // Fires the visit beacon (with surviving UTM params) so admins can
  // attribute viral traffic per-skin via /api/admin/skin-share-stats.
  // Strips the `skin` param from the URL after applying so a refresh
  // doesn't re-pulse. UTM params survive — analytics still see them in
  // the initial pageload.
  useEffect(() => {
    if (!data) return;
    const params = new URLSearchParams(search);
    const targetSkin = params.get("skin");
    if (!targetSkin) return;
    // Harvest the inbound referral code (if present) into sessionStorage
    // so that whenever the visitor eventually hits /register, the
    // referrer gets credited. Survives intra-app nav; per-tab.
    const inboundRef = params.get("ref");
    if (inboundRef) {
      try { sessionStorage.setItem("cosmic_ref_code", inboundRef); } catch { /* noop */ }
    }
    // Fire the share-visit beacon (fire-and-forget; backend silently
    // throttles per-IP and never raises a user-visible error).
    api.post("/skin-share/visit", {
      skin_id: targetSkin,
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
    }).catch(() => { /* analytics must not break the page */ });
    // Defer one frame so the cards are mounted with their data attrs.
    requestAnimationFrame(() => {
      const card = document.querySelector(`[data-skin-card="${targetSkin}"]`);
      if (!card) return;
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      card.classList.add("skin-card-pulse");
      setTimeout(() => card.classList.remove("skin-card-pulse"), 2400);
    });
    // Strip the `skin` AND `ref` params; preserve UTM params for analytics
    params.delete("skin");
    params.delete("ref");
    const next = params.toString();
    navigate({ pathname: "/skins", search: next ? `?${next}` : "" }, { replace: true });
  }, [data, search, navigate]);

  const equip = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/ball-skins/active", { skin_id: id });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't equip comet — try again.");
    } finally {
      setBusy(false);
    }
  };

  const setTrailColor = async (color) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/ball-skins/trail-color", { color });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't set trail color — try again.");
    } finally {
      setBusy(false);
    }
  };

  const setPaddleAccent = async (color) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/cosmetics/paddle-accent", { color });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't set paddle accent — try again.");
    } finally {
      setBusy(false);
    }
  };

  const purchase = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/ball-skins/checkout", {
        skin_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setPayStatus({
        message: e.response?.data?.detail || "Unable to start checkout.",
        tone: "warn",
      });
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.skins;
    return data.skins.filter((s) => s.tier === filter && !(filter === "trophy" && s.unlock?.type === "event"));
  }, [data, filter]);

  if (!data) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest">
          LOADING CATALOG...
        </main>
      </div>
    );
  }

  const unlocked = new Set(data.unlocked);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 fade-in">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-cosmic text-xs tracking-[0.35em] text-[#00ffff] uppercase mb-2">
              // CATALOG · {data.skins.length} COMETS
            </div>
            <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl tracking-tighter">
              Skin Vault
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Earn free skins by piling up score, buy premium ones for ${data.premium_price_usd.toFixed(2)} each, earn legendary drops from monthly top-3 placements, and claim exclusive trophies by completing milestone achievements.
            </p>
          </div>
          <div className="glass px-4 py-3 flex items-center gap-4">
            <Sparkles className="w-4 h-4 text-[#ffd700]" />
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
                Unlocked
              </div>
              <div className="font-heading font-black text-lg text-[#00ff41]">
                {unlocked.size} / {data.skins.length}
              </div>
            </div>
          </div>
        </header>

        {payStatus && (
          <div
            className={`mb-6 glass p-3 border ${
              payStatus.tone === "ok"
                ? "border-[#00ff41]/60 text-[#00ff41]"
                : payStatus.tone === "warn"
                  ? "border-[#ff007f]/60 text-[#ff007f]"
                  : "border-[#00ffff]/60 text-[#00ffff]"
            } font-mono-cosmic text-xs tracking-widest uppercase`}
            data-testid="pay-status"
          >
            {payStatus.message}
          </div>
        )}

        {/* Top-level section toggle: Comet Vault (ball/paddle skins +
            loadouts + shield store) vs. Moonstone Store (Endless-mode
            wallet + items). Loadouts and ShieldStore are tied to the
            comet/cosmetics flow so they only render in that view. */}
        <div
          className="flex items-center gap-2 mb-6 flex-wrap"
          data-testid="skins-view-tabs"
        >
          <button
            type="button"
            onClick={() => setView("comets")}
            className={`px-4 py-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase border transition ${
              view === "comets"
                ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                : "border-white/15 text-white/55 hover:text-white"
            }`}
            data-testid="skins-view-tab-comets"
          >
            // Comet Vault
          </button>
          <button
            type="button"
            onClick={() => setView("moonstone")}
            className={`px-4 py-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase border transition ${
              view === "moonstone"
                ? "border-[#c084fc] text-[#c084fc] bg-[#c084fc]/10"
                : "border-white/15 text-white/55 hover:text-white"
            }`}
            data-testid="skins-view-tab-moonstone"
          >
            ☾ Moonstone Store
          </button>
        </div>

        {view === "moonstone" ? (
          <MoonstoneStore />
        ) : (
          <>

        <LoadoutsPanel
          data={data}
          paddleAccent={paddleAccent}
          onApplied={refresh}
        />

        <HalloweenBundleCard onPurchased={refresh} />

        <ShieldStore />

        {data.active && (
          <div
            className="mb-6 glass p-4 border border-white/15 flex flex-wrap items-center gap-4"
            data-testid="trail-color-picker"
          >
            <div className="flex items-center gap-2 flex-shrink-0">
              <Palette className="w-4 h-4 text-white/60" />
              <div>
                <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
                  Custom Trail
                </div>
                <div className="font-heading uppercase text-sm tracking-tight text-white">
                  Recolor the equipped comet
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {TRAIL_SWATCHES.map((sw) => {
                const active = (data.trail_color || null) === sw.value;
                return (
                  <button
                    key={sw.id}
                    type="button"
                    onClick={() => setTrailColor(sw.value)}
                    className="w-9 h-9 border transition flex items-center justify-center"
                    style={{
                      background: sw.value || "repeating-linear-gradient(45deg, #15151c 0 4px, #2a2a34 4px 8px)",
                      borderColor: active ? "#ffffff" : "rgba(255,255,255,0.25)",
                      boxShadow: active ? `0 0 10px ${sw.value || "rgba(255,255,255,0.4)"}` : "none",
                    }}
                    title={sw.label}
                    data-testid={`trail-swatch-${sw.id}`}
                  >
                    {active && <Check className="w-3.5 h-3.5 text-white drop-shadow" />}
                  </button>
                );
              })}
            </div>
            <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40 ml-auto">
              {data.trail_color ? `Override · ${data.trail_color}` : "Native palette"}
            </span>
          </div>
        )}

        <div
          className="mb-6 glass p-4 border border-white/15 flex flex-wrap items-center gap-4"
          data-testid="paddle-accent-picker"
        >
          <div className="flex items-center gap-2 flex-shrink-0">
            <Palette className="w-4 h-4 text-white/60" />
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
                Paddle Accent
              </div>
              <div className="font-heading uppercase text-sm tracking-tight text-white">
                Recolor the worm
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {TRAIL_SWATCHES.map((sw) => {
              const active = (paddleAccent || null) === sw.value;
              return (
                <button
                  key={sw.id}
                  type="button"
                  onClick={() => setPaddleAccent(sw.value)}
                  className="w-9 h-9 border transition flex items-center justify-center"
                  style={{
                    background: sw.value || "repeating-linear-gradient(45deg, #15151c 0 4px, #2a2a34 4px 8px)",
                    borderColor: active ? "#ffffff" : "rgba(255,255,255,0.25)",
                    boxShadow: active ? `0 0 10px ${sw.value || "rgba(255,255,255,0.4)"}` : "none",
                  }}
                  title={sw.label}
                  data-testid={`paddle-swatch-${sw.id}`}
                >
                  {active && <Check className="w-3.5 h-3.5 text-white drop-shadow" />}
                </button>
              );
            })}
          </div>
          <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40 ml-auto">
            {paddleAccent ? `Override · ${paddleAccent}` : "Native palette"}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="skin-tier-tabs">
          {TIERS.map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-3 py-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase border transition ${
                filter === t.id
                  ? t.id === "premium"
                    ? "border-[#ffd700] text-[#ffd700] bg-[#ffd700]/10"
                    : t.id === "legendary"
                      ? "border-[#b026ff] text-[#b026ff] bg-[#b026ff]/10"
                      : t.id === "trophy"
                        ? "border-[#00ff41] text-[#00ff41] bg-[#00ff41]/10"
                        : t.id === "prismatic"
                          ? "border-[#ff007f] text-[#ff007f] bg-[#ff007f]/10 shadow-[0_0_12px_rgba(255,0,127,0.35)]"
                          : "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                  : "border-white/15 text-white/60 hover:text-white"
              }`}
              data-testid={`skin-tier-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map((s) => {
            const isUnlocked = unlocked.has(s.id);
            const isActive = data.active === s.id;
            const isPaid = s.tier === "premium";
            const isLegend = s.tier === "legendary";
            const isEvent = s.unlock?.type === "event";
            const isTrophy = s.tier === "trophy" && !isEvent;
            const isPrism = s.tier === "prismatic";
            // Celebrate the Sector Reaper unlock — gold/crimson glow ring,
            // crown sparkle, and a "6/6 BOSSES" ribbon. Only fires when
            // the pilot has actually earned it (not just for show on locked
            // cards or for other trophy skins).
            const isReaperVictor = s.id === "reaper-crown" && isUnlocked;
            // Prismatic ball-skin cards always render with a rainbow
            // ring (locked or unlocked) so the rarity is unmistakable.
            const prismGlow = isPrism && isUnlocked;
            return (
              <div
                key={s.id}
                className={`glass p-3 flex flex-col gap-3 transition relative ${
                  isReaperVictor
                    ? "border-[#ffd700] shadow-[0_0_22px_rgba(255,215,0,0.45)]"
                    : prismGlow
                      ? "border-[#ff007f] shadow-[0_0_22px_rgba(255,0,127,0.45)]"
                      : isPrism
                        ? "border-[#ff007f]/50"
                        : isActive
                          ? "border-[#00ffff] shadow-[0_0_14px_rgba(0,255,255,0.3)]"
                          : "border-white/10 hover:border-white/25"
                }`}
                style={
                  isReaperVictor
                    ? { background: "linear-gradient(180deg, rgba(255,215,0,0.08) 0%, rgba(255,0,127,0.06) 100%)" }
                    : isPrism
                      ? { background: "linear-gradient(140deg, rgba(103,232,249,0.06) 0%, rgba(176,38,255,0.06) 50%, rgba(255,0,127,0.08) 100%)" }
                      : undefined
                }
                data-testid={`skin-card-${s.id}`}
                data-skin-card={s.id}
              >
                {isReaperVictor && (
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 border border-[#ffd700] bg-[#08080e] font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-[#ffd700] inline-flex items-center gap-1 whitespace-nowrap z-10"
                    style={{ boxShadow: "0 0 10px rgba(255,215,0,0.6)" }}
                    data-testid="reaper-victor-ribbon"
                  >
                    <Trophy className="w-2.5 h-2.5" /> 6/6 Bosses Defeated
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {isPaid && <Tag className="w-3 h-3 text-[#ffd700]" />}
                    {isLegend && <Trophy className="w-3 h-3 text-[#b026ff]" />}
                    {isTrophy && <Trophy className="w-3 h-3 text-[#00ff41]" />}
                    {isPrism && <Sparkles className="w-3 h-3 text-[#ff007f]" />}
                    {isEvent && <Tag className="w-3 h-3 text-[#ff8a00]" />}
                    <span
                      data-testid={`skin-tier-label-${s.id}`}
                      className={`font-mono-cosmic text-[8px] tracking-[0.3em] uppercase ${
                        isEvent
                          ? "text-[#ff8a00]"
                          : isPaid
                          ? "text-[#ffd700]"
                          : isLegend
                            ? "text-[#b026ff]"
                            : isTrophy
                              ? "text-[#00ff41]"
                              : isPrism
                                ? "text-[#ff007f]"
                                : "text-[#00ffff]"
                      }`}
                    >
                      {isEvent ? "event" : s.tier}
                    </span>
                  </div>
                  {isActive ? (
                    <Check className="w-3.5 h-3.5 text-[#00ffff]" />
                  ) : !isUnlocked ? (
                    <Lock className="w-3.5 h-3.5 text-white/40" />
                  ) : null}
                </div>
                <div className="flex items-center justify-center">
                  <SkinPreview
                    skin={isActive ? applyTrailColor(s, data.trail_color) : s}
                    size={100}
                  />
                </div>
                <div>
                  <div
                    className="font-heading uppercase text-sm tracking-tight text-white leading-tight"
                    style={{
                      // 2-line clamp so long names ("AZURE ASCENDANT",
                      // "REAPER CROWN", "PERFECT MONTH CROWN") wrap on
                      // narrow mobile cards instead of truncating with
                      // the ugly "AZURE ASCEN..." ellipsis.
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minHeight: "2.6em",
                    }}
                    title={s.name}
                  >
                    {s.name}
                  </div>
                  <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50 mt-0.5 truncate">
                    {isUnlocked
                      ? "Unlocked"
                      : s.unlock.type === "score"
                        ? `${s.unlock.value.toLocaleString()} pts to unlock`
                        : s.unlock.type === "monthly_top"
                          ? "Top 3 monthly reward"
                          : s.unlock.type === "achievement"
                            ? `Earn: ${ACH_NAME[s.unlock.value] || s.unlock.value}`
                            : s.unlock.type === "boss_drop"
                              ? `Defeat ${PRISMATIC_SECTOR_LABEL[s.unlock.value] || `Boss · Lvl ${s.unlock.value}`}`
                              : s.unlock.type === "season_pass"
                                ? "Season Pass reward"
                                : s.unlock.type === "event"
                                  ? `Halloween Bundle · $${(s.unlock.price || 4.99).toFixed(2)}`
                                  : isPaid
                                    ? `$${data.premium_price_usd.toFixed(2)}`
                                    : "Locked"}
                  </div>
                  {/* Lore card — a 1-line in-app blurb that mythologizes
                      select trophy unlocks. Only renders when an entry
                      exists in SKIN_LORE. Italic, sub-muted so it doesn't
                      compete with the unlock criterion above it. */}
                  {SKIN_LORE[s.id] && (
                    <p
                      className="mt-1.5 text-[10px] leading-snug text-white/55 italic line-clamp-3"
                      data-testid={`skin-lore-${s.id}`}
                    >
                      {SKIN_LORE[s.id]}
                    </p>
                  )}
                </div>
                {isActive ? (
                  <div className="flex gap-2">
                    <button
                      disabled
                      className="flex-1 py-2 border border-[#00ffff] text-[#00ffff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase"
                      data-testid={`skin-equipped-${s.id}`}
                    >
                      Equipped
                    </button>
                    <ShareButton skin={s} refCode={refCode} />
                  </div>
                ) : isUnlocked ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => equip(s.id)}
                      disabled={busy}
                      className="flex-1 py-2 border border-white/20 hover:border-[#00ffff] hover:text-[#00ffff] text-white/80 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition"
                      data-testid={`skin-equip-${s.id}`}
                    >
                      Equip
                    </button>
                    <ShareButton skin={s} refCode={refCode} />
                  </div>
                ) : isPaid ? (
                  <button
                    onClick={() => purchase(s.id)}
                    disabled={busy}
                    className="w-full py-2 border border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center justify-center gap-2"
                    data-testid={`skin-buy-${s.id}`}
                  >
                    <ShoppingCart className="w-3 h-3" /> Buy ${data.premium_price_usd.toFixed(2)}
                  </button>
                ) : s.unlock.type === "event" ? (
                  <button
                    onClick={() => {
                      const el = document.querySelector('[data-testid="halloween-event-card"]');
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    className="w-full py-2 border border-[#ff6a00] text-[#ff8a00] hover:bg-[#ff6a00]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center justify-center gap-2"
                    data-testid={`skin-event-buy-${s.id}`}
                  >
                    <ShoppingCart className="w-3 h-3" /> Event · ${(s.unlock.price || 4.99).toFixed(2)}
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full py-2 border border-white/10 text-white/30 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase cursor-not-allowed"
                  >
                    Locked
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
